import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TVNavigationService } from './tv-navigation.service';
import { TvBackButtonService, OverlayType } from './tv-back-button.service';

export interface KeyboardConfig {
  initialValue: string;
  inputType: 'text' | 'tel' | 'email' | 'number';
  maxLength?: number;
  placeholder?: string;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  onSuggestionSelect?: (suggestion: string) => void;
}

/**
 * TvKeyboardService - Manages on-screen keyboard state for TV navigation
 *
 * Coordinates between:
 * - Form fields (trigger keyboard on Enter)
 * - Grid keyboard component (show/hide, value changes)
 * - TVNavigationService (focus restoration after keyboard closes)
 */
@Injectable({ providedIn: 'root' })
export class TvKeyboardService {
  private isVisibleSubject = new BehaviorSubject<boolean>(false);
  private configSubject = new BehaviorSubject<KeyboardConfig | null>(null);
  private currentValueSubject = new BehaviorSubject<string>('');
  private suggestionsSubject = new BehaviorSubject<string[]>([]);

  /** Observable for keyboard visibility */
  public isVisible$ = this.isVisibleSubject.asObservable();

  /** Observable for current keyboard config */
  public config$ = this.configSubject.asObservable();

  /** Observable for current input value */
  public currentValue$ = this.currentValueSubject.asObservable();

  /** Observable for suggestions list */
  public suggestions$ = this.suggestionsSubject.asObservable();

  /** Saved focus context for restoration */
  private savedFocusContext: { regionId: string; index: number } | null = null;

  /** Overlay ID for back button registration */
  private readonly KEYBOARD_OVERLAY_ID = 'tv-keyboard-overlay';

  constructor(
    private zone: NgZone,
    private tvNav: TVNavigationService,
    private backButton: TvBackButtonService
  ) {}

  /**
   * Show the keyboard with the given configuration
   * Saves current focus context for restoration on close
   * Suppresses TVNavigationService to prevent D-pad conflicts
   */
  show(config: KeyboardConfig): void {
    // Save current focus for restoration
    this.savedFocusContext = {
      regionId: this.tvNav.getActiveRegionId() || '',
      index: this.tvNav.getFocusedIndex()
    };

    // Suppress navigation service so keyboard gets exclusive D-pad control
    this.tvNav.suppressNavigation();

    // Register keyboard as overlay with back button service
    this.backButton.pushOverlay({
      id: this.KEYBOARD_OVERLAY_ID,
      type: OverlayType.KEYBOARD,
      onClose: () => {
        this.cancel();
        setTimeout(() => this.restoreFocus(), 100);
      }
    });

    this.zone.run(() => {
      this.configSubject.next(config);
      this.currentValueSubject.next(config.initialValue || '');
      this.isVisibleSubject.next(true);
    });
  }

  /**
   * Hide the keyboard
   * Does NOT restore focus - caller should handle via onSubmit/onCancel callbacks
   * Resumes TVNavigationService D-pad handling
   */
  hide(): void {
    // Unregister keyboard overlay from back button service
    this.backButton.popOverlay(this.KEYBOARD_OVERLAY_ID);

    // Resume navigation service so form/content D-pad works again
    this.tvNav.resumeNavigation();

    this.zone.run(() => {
      this.isVisibleSubject.next(false);
      this.configSubject.next(null);
      this.suggestionsSubject.next([]);
      this.currentValueSubject.next('');
    });
  }

  /**
   * Update the current value (called by keyboard component on key press)
   */
  updateValue(value: string): void {
    this.currentValueSubject.next(value);
  }

  /**
   * Get current value synchronously
   */
  getCurrentValue(): string {
    return this.currentValueSubject.getValue();
  }

  /**
   * Check if keyboard is currently visible
   */
  isVisible(): boolean {
    return this.isVisibleSubject.getValue();
  }

  /**
   * Get saved focus context for restoration
   */
  getSavedFocusContext(): { regionId: string; index: number } | null {
    return this.savedFocusContext;
  }

  /**
   * Restore focus to the saved context
   * Called after keyboard closes to return focus to form field
   */
  restoreFocus(): void {
    if (this.savedFocusContext && this.savedFocusContext.regionId) {
      this.tvNav.activateRegion(
        this.savedFocusContext.regionId,
        this.savedFocusContext.index
      );
    }
    this.savedFocusContext = null;
  }

  /**
   * Submit the current value and close keyboard
   */
  submit(): void {
    const config = this.configSubject.getValue();
    const value = this.currentValueSubject.getValue();

    if (config?.onSubmit) {
      config.onSubmit(value);
    }

    this.hide();
  }

  /**
   * Cancel input and close keyboard
   */
  cancel(): void {
    const config = this.configSubject.getValue();

    if (config?.onCancel) {
      config.onCancel();
    }

    this.hide();
  }

  /**
   * Update the suggestions list displayed alongside the keyboard
   */
  setSuggestions(suggestions: string[]): void {
    this.suggestionsSubject.next(suggestions);
  }

  /**
   * Select a suggestion — calls onSuggestionSelect callback and closes keyboard
   */
  selectSuggestion(suggestion: string): void {
    const config = this.configSubject.getValue();
    if (config?.onSuggestionSelect) {
      config.onSuggestionSelect(suggestion);
    }
    this.hide();
  }

  /**
   * Clear the current input value
   */
  clear(): void {
    this.currentValueSubject.next('');
  }

  /**
   * Append a character to the current value
   */
  appendChar(char: string): void {
    const config = this.configSubject.getValue();
    const current = this.currentValueSubject.getValue();

    // Respect maxLength if set
    if (config?.maxLength && current.length >= config.maxLength) {
      return;
    }

    this.currentValueSubject.next(current + char);
  }

  /**
   * Delete the last character from the current value
   */
  backspace(): void {
    const current = this.currentValueSubject.getValue();
    if (current.length > 0) {
      this.currentValueSubject.next(current.slice(0, -1));
    }
  }
}
